import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email is registered, a reset link is on its way");
    } catch (err) {
      // Endpoint always returns 200 to prevent enumeration — this should be rare.
      toast.error(err?.response?.data?.detail || "Could not process request — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-16">
        <Button variant="ghost" size="sm" onClick={() => nav("/login")}
                className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
                data-testid="forgot-password-back">
          <ArrowLeft size={14} /> Back to sign in
        </Button>
        <div className="mb-8">
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight"
              data-testid="forgot-password-title">
            Forgot your password?
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Enter the email you signed up with. If we find an account, we'll email
            a one-time reset link that works for the next 60 minutes.
          </p>
        </div>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8">
            {sent ? (
              <div className="text-center py-4 space-y-3" data-testid="forgot-password-sent">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle2 size={28} />
                </div>
                <div className="font-display font-semibold text-lg">Check your inbox</div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  If <span className="font-medium text-foreground">{email}</span> is registered
                  with Manodayam, you'll receive a reset link within a minute or two. The link
                  works once and expires in 60 minutes.
                </p>
                <div className="pt-3">
                  <Link to="/login" className="text-primary underline underline-offset-4 text-sm"
                        data-testid="forgot-password-back-link">
                    Back to sign in
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         placeholder="you@example.com"
                         data-testid="forgot-password-email" />
                </div>
                <Button type="submit" size="lg" disabled={submitting || !email}
                        className="rounded-full w-full sm:w-auto px-10 mano-lift"
                        data-testid="forgot-password-submit">
                  <Mail size={14} /> {submitting ? "Sending…" : "Email reset link"}
                </Button>
                <div className="text-sm text-muted-foreground pt-2">
                  Signed in with Google?{" "}
                  <span>Manage your password on Google, then return here.</span>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
